import React, { useEffect, useRef } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';

import { ThemedText } from '@/components/themed-text';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { AutomaticCheckout } from '@/components/checkout/automatic-checkout';
import { useToastStore } from '@/store/toastStore';
import api from '@/services/api';

/** Recorte de `GET /charge/:id/public` que o checkout automático usa. */
interface PublicAutomaticCharge {
  status: string;
  stripeAccount?: string | null;
  publishableKey?: string | null;
}

interface AutomaticPaymentSectionProps {
  chargeId: string;
  /** Pagamento confirmado pelo webhook (o polling viu `PAID`). */
  onPaid?: () => void;
}

/**
 * Checkout automático dentro do app do cliente (Pagamentos e detalhe do
 * contrato): o mesmo `AutomaticCheckout` do `/checkout/:id` público — Payment
 * Element embutido na web, PaymentSheet no nativo —, sem mandar o cliente
 * para fora. As formas (cartão, Pix, boleto) são as que a API liberou para
 * esta cobrança. Sem "pagar direto ao prestador": cobrança automática é
 * escolha do prestador. Subconta e chave vêm do `GET /charge/:id/public` (o
 * `ChargeResponseDto` autenticado não as traz); o mesmo GET faz o polling de
 * status, porque quem marca pago é o webhook (T2.3).
 */
export function AutomaticPaymentSection({ chargeId, onPaid }: AutomaticPaymentSectionProps) {
  const { addToast } = useToastStore();
  const paidNotifiedRef = useRef(false);

  const { data, isLoading } = useQuery({
    queryKey: ['charge-public', chargeId],
    queryFn: async () => (await api.get<PublicAutomaticCharge>(`/charge/${chargeId}/public`)).data,
    refetchInterval: (query) => (query.state.data?.status === 'PAID' ? false : 5000),
  });

  useEffect(() => {
    if (data?.status === 'PAID' && !paidNotifiedRef.current) {
      paidNotifiedRef.current = true;
      addToast('Pagamento confirmado!', 'success');
      onPaid?.();
    }
  }, [data?.status, addToast, onPaid]);

  if (isLoading || !data) {
    return (
      <View className="items-center py-6">
        <LoadingSpinner message="Preparando pagamento..." />
      </View>
    );
  }

  if (!data.stripeAccount || !data.publishableKey) {
    return (
      <ThemedText className="text-sm text-neutral-500">
        O pagamento não está disponível agora. Fale com o prestador.
      </ThemedText>
    );
  }

  return (
    <AutomaticCheckout
      chargeId={chargeId}
      stripeAccount={data.stripeAccount}
      publishableKey={data.publishableKey}
    />
  );
}
